import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types for mineral discovery state
export interface RuleWeight {
  ruleName: string;
  weight: number;
  enabled: boolean;
  description: string;
}

export interface AnalysisConfiguration {
  commodity: 'gold' | 'silver' | 'copper';
  region: string;
  ruleWeights: RuleWeight[];
  analysisArea: {
    bounds: [number, number, number, number]; // [minLat, minLon, maxLat, maxLon]
    gridResolution: number;
  };
  outputFormats: string[];
  confidenceThreshold: number;
  modelVersion?: string; // Optional model version
}

export interface AnalysisStatus {
  id: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: number;
  message: string;
  estimatedTimeRemaining?: number;
  startTime?: Date;
}

export interface ProbabilityGrid {
  bounds: [number, number, number, number];
  gridSize: [number, number];
  probabilityMatrix: number[][];
  confidenceMatrix: number[][];
  metadata: {
    commodity: string;
    maxScore: number;
    meanScore: number;
    highPotentialCells: number;
    analysisDate: string;
  };
}

export interface HighPotentialArea {
  id: string;
  centroid: [number, number];
  area_km2: number;
  maxScore: number;
  meanScore: number;
  meanConfidence: number;
  priorityRank: number;
  geometry: any;
}

export interface AnalysisResult {
  id: string;
  analysisId: string;
  timestamp: string;
  configuration: AnalysisConfiguration;
  results: {
    totalAreas: number;
    highPotentialAreas: number;
    averageScore: number;
    maxScore: number;
    processingTime: number;
  };
  data: {
    probabilityGrid: ProbabilityGrid;
    highPotentialAreas: HighPotentialArea[];
  };
}

interface MineralDiscoveryState {
  // Configuration state
  configuration: AnalysisConfiguration;
  selectedPreset: string;
  
  // Analysis state
  analysisStatus: AnalysisStatus;
  currentAnalysisId: string | null;
  
  // Results state
  analysisResults: AnalysisResult[];
  selectedResultId: string | null;
  probabilityData: ProbabilityGrid | null;
  highPotentialAreas: HighPotentialArea[];
  
  // UI state
  panelOpen: boolean;
  resultsViewerOpen: boolean;
  probabilityLayerVisible: boolean;
  activeStep: number;
  showAdvanced: boolean;
  
  // Layer controls
  layerOpacity: number;
  probabilityThreshold: number;
  colorScale: string;
  showConfidence: boolean;
  showAreas: boolean;
  
  // Export state
  exportInProgress: boolean;
  exportProgress: number;
}

// Default rule weights for each commodity
const DEFAULT_RULE_WEIGHTS: { [key: string]: RuleWeight[] } = {
  gold: [
    { ruleName: 'sericite_alteration', weight: 25, enabled: true, description: 'SWIR spectral signature of sericite alteration' },
    { ruleName: 'iron_oxide_alteration', weight: 20, enabled: true, description: 'Red/Blue ratio indicating iron oxide minerals' },
    { ruleName: 'gold_geochemical_anomaly', weight: 30, enabled: true, description: 'Elevated gold in stream sediments or soils' },
    { ruleName: 'fault_proximity_gold', weight: 20, enabled: true, description: 'Distance to mapped fault structures' },
    { ruleName: 'favorable_host_gold', weight: 15, enabled: true, description: 'Metamorphic or volcanic host rocks' },
    { ruleName: 'arsenic_pathfinder', weight: 15, enabled: false, description: 'Arsenic as pathfinder element for gold' }
  ],
  silver: [
    { ruleName: 'silver_geochemical_anomaly', weight: 30, enabled: true, description: 'Elevated silver in geochemical surveys' },
    { ruleName: 'clay_alteration', weight: 15, enabled: true, description: 'SWIR spectral signature of clay alteration' },
    { ruleName: 'fault_proximity_gold', weight: 20, enabled: true, description: 'Structural controls for epithermal systems' },
    { ruleName: 'iron_oxide_alteration', weight: 15, enabled: true, description: 'Iron oxide alteration halos' }
  ],
  copper: [
    { ruleName: 'copper_geochemical_anomaly', weight: 35, enabled: true, description: 'Elevated copper in geochemical data' },
    { ruleName: 'molybdenum_pathfinder', weight: 20, enabled: true, description: 'Molybdenum association in porphyry systems' },
    { ruleName: 'favorable_host_copper', weight: 20, enabled: true, description: 'Intrusive or porphyritic host rocks' },
    { ruleName: 'iron_oxide_alteration', weight: 15, enabled: false, description: 'Propylitic alteration zones' }
  ]
};

const initialState: MineralDiscoveryState = {
  configuration: {
    commodity: 'gold',
    region: 'South America',
    ruleWeights: DEFAULT_RULE_WEIGHTS.gold,
    analysisArea: {
      bounds: [-30, -75, -15, -45],
      gridResolution: 0.01
    },
    outputFormats: ['geojson'],
    confidenceThreshold: 0.6
  },
  selectedPreset: '',
  analysisStatus: {
    id: '',
    status: 'idle',
    progress: 0,
    message: 'Ready to start analysis'
  },
  currentAnalysisId: null,
  analysisResults: [],
  selectedResultId: null,
  probabilityData: null,
  highPotentialAreas: [],
  panelOpen: false,
  resultsViewerOpen: false,
  probabilityLayerVisible: false,
  activeStep: 0,
  showAdvanced: false,
  layerOpacity: 0.7,
  probabilityThreshold: 0.6,
  colorScale: 'heat',
  showConfidence: false,
  showAreas: true,
  exportInProgress: false,
  exportProgress: 0
};

// Async thunks for API calls
export const runMineralDiscoveryAnalysis = createAsyncThunk(
  'mineralDiscovery/runAnalysis',
  async (configuration: AnalysisConfiguration, { rejectWithValue }) => {
    try {
      const response = await fetch('http://localhost:8000/api/enhanced-analysis/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...configuration,
          modelVersion: configuration.modelVersion || 'v1.0.0',
          manual_trigger: true
        }),
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const stopMineralDiscoveryAnalysis = createAsyncThunk(
  'mineralDiscovery/stopAnalysis',
  async (analysisId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`http://localhost:8000/api/mineral-discovery/stop-analysis/${analysisId}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Failed to stop analysis: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const exportAnalysisResults = createAsyncThunk(
  'mineralDiscovery/exportResults',
  async ({ resultId, format }: { resultId: string; format: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`http://localhost:8000/api/mineral-discovery/export/${resultId}?format=${format}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

const mineralDiscoverySlice = createSlice({
  name: 'mineralDiscovery',
  initialState,
  reducers: {
    // Configuration actions
    setConfiguration: (state, action: PayloadAction<Partial<AnalysisConfiguration>>) => {
      state.configuration = { ...state.configuration, ...action.payload };
    },
    setCommodity: (state, action: PayloadAction<'gold' | 'silver' | 'copper'>) => {
      state.configuration.commodity = action.payload;
      state.configuration.ruleWeights = DEFAULT_RULE_WEIGHTS[action.payload];
    },
    setModelVersion: (state, action: PayloadAction<string>) => {
      state.configuration.modelVersion = action.payload;
    },
    setRuleWeight: (state, action: PayloadAction<{ ruleName: string; weight: number }>) => {
      const rule = state.configuration.ruleWeights.find(r => r.ruleName === action.payload.ruleName);
      if (rule) {
        rule.weight = action.payload.weight;
      }
    },
    toggleRule: (state, action: PayloadAction<{ ruleName: string; enabled: boolean }>) => {
      const rule = state.configuration.ruleWeights.find(r => r.ruleName === action.payload.ruleName);
      if (rule) {
        rule.enabled = action.payload.enabled;
      }
    },
    setAnalysisArea: (state, action: PayloadAction<{
      bounds?: [number, number, number, number];
      gridResolution?: number;
    }>) => {
      if (action.payload.bounds) {
        state.configuration.analysisArea.bounds = action.payload.bounds;
      }
      if (action.payload.gridResolution !== undefined) {
        state.configuration.analysisArea.gridResolution = action.payload.gridResolution;
      }
    },
    setConfidenceThreshold: (state, action: PayloadAction<number>) => {
      state.configuration.confidenceThreshold = action.payload;
    },
    setSelectedPreset: (state, action: PayloadAction<string>) => {
      state.selectedPreset = action.payload;
    },

    // Analysis status actions
    setAnalysisStatus: (state, action: PayloadAction<Partial<AnalysisStatus>>) => {
      state.analysisStatus = { ...state.analysisStatus, ...action.payload };
    },
    updateAnalysisProgress: (state, action: PayloadAction<{
      progress: number;
      message: string;
      estimatedTimeRemaining?: number;
    }>) => {
      state.analysisStatus.progress = action.payload.progress;
      state.analysisStatus.message = action.payload.message;
      if (action.payload.estimatedTimeRemaining !== undefined) {
        state.analysisStatus.estimatedTimeRemaining = action.payload.estimatedTimeRemaining;
      }
    },

    // Results actions
    addAnalysisResult: (state, action: PayloadAction<AnalysisResult>) => {
      state.analysisResults.push(action.payload);
      state.selectedResultId = action.payload.id;
      state.probabilityData = action.payload.data.probabilityGrid;
      state.highPotentialAreas = action.payload.data.highPotentialAreas;
    },
    setSelectedResult: (state, action: PayloadAction<string>) => {
      state.selectedResultId = action.payload;
      const result = state.analysisResults.find(r => r.id === action.payload);
      if (result) {
        state.probabilityData = result.data.probabilityGrid;
        state.highPotentialAreas = result.data.highPotentialAreas;
      }
    },
    setProbabilityData: (state, action: PayloadAction<ProbabilityGrid | null>) => {
      state.probabilityData = action.payload;
    },
    setHighPotentialAreas: (state, action: PayloadAction<HighPotentialArea[]>) => {
      state.highPotentialAreas = action.payload;
    },
    clearResults: (state) => {
      state.analysisResults = [];
      state.selectedResultId = null;
      state.probabilityData = null;
      state.highPotentialAreas = [];
    },

    // UI actions
    setPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.panelOpen = action.payload;
    },
    setResultsViewerOpen: (state, action: PayloadAction<boolean>) => {
      state.resultsViewerOpen = action.payload;
    },
    setProbabilityLayerVisible: (state, action: PayloadAction<boolean>) => {
      state.probabilityLayerVisible = action.payload;
    },
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },
    setShowAdvanced: (state, action: PayloadAction<boolean>) => {
      state.showAdvanced = action.payload;
    },

    // Layer control actions
    setLayerOpacity: (state, action: PayloadAction<number>) => {
      state.layerOpacity = action.payload;
    },
    setProbabilityThreshold: (state, action: PayloadAction<number>) => {
      state.probabilityThreshold = action.payload;
    },
    setColorScale: (state, action: PayloadAction<string>) => {
      state.colorScale = action.payload;
    },
    setShowConfidence: (state, action: PayloadAction<boolean>) => {
      state.showConfidence = action.payload;
    },
    setShowAreas: (state, action: PayloadAction<boolean>) => {
      state.showAreas = action.payload;
    },

    // Export actions
    setExportProgress: (state, action: PayloadAction<{ inProgress: boolean; progress: number }>) => {
      state.exportInProgress = action.payload.inProgress;
      state.exportProgress = action.payload.progress;
    },

    // Reset actions
    resetConfiguration: (state) => {
      state.configuration = initialState.configuration;
      state.selectedPreset = '';
      state.activeStep = 0;
    },
    resetAnalysis: (state) => {
      state.analysisStatus = initialState.analysisStatus;
      state.currentAnalysisId = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Run analysis
      .addCase(runMineralDiscoveryAnalysis.pending, (state) => {
        state.analysisStatus = {
          id: `analysis_${Date.now()}`,
          status: 'running',
          progress: 0,
          message: 'Initializing analysis...',
          startTime: new Date()
        };
      })
      .addCase(runMineralDiscoveryAnalysis.fulfilled, (state, action) => {
        state.currentAnalysisId = action.payload.analysisId;
        state.analysisStatus.id = action.payload.analysisId;
        state.analysisStatus.message = 'Analysis started successfully';
      })
      .addCase(runMineralDiscoveryAnalysis.rejected, (state, action) => {
        state.analysisStatus.status = 'failed';
        state.analysisStatus.message = `Error: ${action.payload}`;
      })
      
      // Stop analysis
      .addCase(stopMineralDiscoveryAnalysis.fulfilled, (state) => {
        state.analysisStatus.status = 'idle';
        state.analysisStatus.message = 'Analysis stopped by user';
        state.currentAnalysisId = null;
      })
      
      // Export results
      .addCase(exportAnalysisResults.pending, (state) => {
        state.exportInProgress = true;
        state.exportProgress = 0;
      })
      .addCase(exportAnalysisResults.fulfilled, (state) => {
        state.exportInProgress = false;
        state.exportProgress = 100;
      })
      .addCase(exportAnalysisResults.rejected, (state) => {
        state.exportInProgress = false;
        state.exportProgress = 0;
      });
  }
});

export const {
  setConfiguration,
  setCommodity,
  setModelVersion,
  setRuleWeight,
  toggleRule,
  setAnalysisArea,
  setConfidenceThreshold,
  setSelectedPreset,
  setAnalysisStatus,
  updateAnalysisProgress,
  addAnalysisResult,
  setSelectedResult,
  setProbabilityData,
  setHighPotentialAreas,
  clearResults,
  setPanelOpen,
  setResultsViewerOpen,
  setProbabilityLayerVisible,
  setActiveStep,
  setShowAdvanced,
  setLayerOpacity,
  setProbabilityThreshold,
  setColorScale,
  setShowConfidence,
  setShowAreas,
  setExportProgress,
  resetConfiguration,
  resetAnalysis
} = mineralDiscoverySlice.actions;

export default mineralDiscoverySlice.reducer;
